import type { Point } from "../scene/model";
import type { PathAnchor } from "./vector-path";
import { penAnchors, simplify } from "./pen-path";

type Keyframe = { anchors: PathAnchor[]; knots: number[] };
const mix = (a: Point, b: Point, t: number): Point => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});
const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);
const mapAnchor = (a: PathAnchor, fn: (p: Point) => Point): PathAnchor => ({
  ...fn(a),
  ...(a.incoming ? { incoming: fn(a.incoming) } : {}),
  ...(a.outgoing ? { outgoing: fn(a.outgoing) } : {}),
});

function reduce(points: Point[], tolerance: number, closed: boolean) {
  if (!closed) return simplify(points, tolerance);
  let opposite = 1;
  for (let i = 2; i < points.length; i++)
    if (
      distance(points[0]!, points[i]!) > distance(points[0]!, points[opposite]!)
    )
      opposite = i;
  const first = simplify(points.slice(0, opposite + 1), tolerance),
    second = simplify([...points.slice(opposite), points[0]!], tolerance);
  const result = [...first.slice(0, -1), ...second.slice(0, -1)];
  if (result.length >= 3) return result;
  let third = opposite === 1 ? 2 : 1;
  const a = points[0]!,
    b = points[opposite]!,
    area = (p: Point) =>
      Math.abs((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x));
  for (let i = 1; i < points.length; i++)
    if (i !== opposite && area(points[i]!) > area(points[third]!)) third = i;
  return [0, opposite, third].sort((a, b) => a - b).map((i) => points[i]!);
}

/** Exact de Casteljau subdivision makes knot removal visually continuous. */
function split(
  c: [Point, Point, Point, Point],
  t: number,
): [[Point, Point, Point, Point], [Point, Point, Point, Point]] {
  const a = mix(c[0], c[1], t),
    b = mix(c[1], c[2], t),
    d = mix(c[2], c[3], t),
    e = mix(a, b, t),
    f = mix(b, d, t),
    p = mix(e, f, t);
  return [
    [c[0], a, e, p],
    [p, f, d, c[3]],
  ];
}
function subdivide(
  frame: Keyframe,
  knots: number[],
  closed: boolean,
): PathAnchor[] {
  const result: PathAnchor[] = [];
  let target = 0;
  for (let i = 0; i < frame.anchors.length - (closed ? 0 : 1); i++) {
    const a = frame.anchors[i]!,
      b = frame.anchors[(i + 1) % frame.anchors.length]!,
      end = frame.knots[i + 1] ?? 1;
    let start = frame.knots[i]!,
      c: [Point, Point, Point, Point] = [
        a,
        a.outgoing ?? a,
        b.incoming ?? b,
        b,
      ];
    if (!result.length) result.push({ x: a.x, y: a.y });
    while (target < knots.length - 1 && knots[target + 1]! <= end + 1e-12) {
      const next = knots[++target]!,
        t = Math.min(1, Math.max(0, (next - start) / (end - start || 1))),
        parts = split(c, t),
        piece = parts[0];
      result.at(-1)!.outgoing = piece[1];
      result.push({ ...piece[3], incoming: piece[2] });
      c = parts[1];
      start = next;
      if (next >= end - 1e-12) break;
    }
    if (closed && i === frame.anchors.length - 1) {
      result.at(-1)!.outgoing = c[1];
      result[0]!.incoming = c[2];
    }
  }
  return result;
}

// Source arrays are immutable scene values; the weak cache follows their lifetime.
// Retain at most the two currently needed keyframes for each source, not 101 full contours.
const cache = new WeakMap<
  PathAnchor[],
  {
    width: number;
    height: number;
    closed: boolean;
    original: PathAnchor[];
    knots: number[];
    frames: Map<number, Keyframe>;
  }
>();
export function smoothPenContour(
  source: PathAnchor[],
  width: number,
  height: number,
  closed: boolean,
  amount: number,
): PathAnchor[] {
  if (amount <= 0 || source.length <= 2) return source;
  let model = cache.get(source);
  if (
    !model ||
    model.width !== width ||
    model.height !== height ||
    model.closed !== closed
  ) {
    const original = source.map((a) =>
        mapAnchor(a, (p) => ({ x: p.x * width, y: p.y * height })),
      ),
      knots = [0];
    for (let i = 1; i < original.length; i++)
      knots.push(
        knots[i - 1]! +
          Math.max(1e-8, distance(original[i - 1]!, original[i]!)),
      );
    const total =
      knots.at(-1)! +
      (closed ? Math.max(1e-8, distance(original.at(-1)!, original[0]!)) : 0);
    model = {
      width,
      height,
      closed,
      original,
      knots: knots.map((t) => t / (total || 1)),
      frames: new Map(),
    };
    cache.set(source, model);
  }
  const { original, knots, frames } = model;
  const frame = (level: number): Keyframe => {
    if (level === 0) return { anchors: original, knots };
    const existing = frames.get(level);
    if (existing) return existing;
    const strength = level / 10,
      reduced = reduce(
        original,
        Math.hypot(width, height) * 0.035 * strength ** 2,
        closed,
      );
    const indices = new Map(original.map((a, i) => [a, i]));
    const softened = reduced.map((p, i) => {
      if (!closed && (!i || i === reduced.length - 1)) return p;
      const prev = reduced[(i + reduced.length - 1) % reduced.length]!,
        next = reduced[(i + 1) % reduced.length]!,
        a = distance(p, prev),
        b = distance(p, next),
        total = a + b || 1;
      return mix(
        p,
        {
          x: (prev.x * b + next.x * a) / total,
          y: (prev.y * b + next.y * a) / total,
        },
        strength * 0.28,
      );
    });
    const anchors = penAnchors(softened, closed, strength);
    const result = {
      anchors,
      knots: reduced.map((p) => knots[indices.get(p)!]!),
    };
    frames.set(level, result);
    return result;
  };
  const position = Math.min(10, amount / 10),
    lo = Math.floor(position),
    hi = Math.min(10, lo + 1),
    blend = position - lo;
  for (const key of frames.keys())
    if (key !== lo && key !== hi) frames.delete(key);
  const from = frame(lo),
    to = frame(hi);
  let anchors = from.anchors;
  if (blend > 0) {
    const expanded = subdivide(to, from.knots, closed);
    anchors = from.anchors.map((a, i) => {
      const b = expanded[i]!;
      return {
        ...mix(a, b, blend),
        ...(a.incoming || b.incoming
          ? { incoming: mix(a.incoming ?? a, b.incoming ?? b, blend) }
          : {}),
        ...(a.outgoing || b.outgoing
          ? { outgoing: mix(a.outgoing ?? a, b.outgoing ?? b, blend) }
          : {}),
      };
    });
  }
  return anchors.map((a) =>
    mapAnchor(a, (p) => ({ x: p.x / width, y: p.y / height })),
  );
}
